import { Button, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface VersionToggleProps {
  /** Where the button navigates to. */
  to: string;
  /** 'classic' => this is the new page, offer the old one. 'new' => reverse. */
  variant: 'classic' | 'new';
}

/**
 * Small pill that lets the owner jump between the revamped page and the
 * original ("classic") one so they can compare the two side by side.
 */
const VersionToggle = ({ to, variant }: VersionToggleProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isClassicTarget = variant === 'classic';

  return (
    <Tooltip
      title={
        isClassicTarget
          ? t('versionToggle.viewClassicHint', 'See the previous design of this page')
          : t('versionToggle.viewNewHint', 'Back to the refreshed design')
      }
    >
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={() => navigate(to)}
        startIcon={isClassicTarget ? <HistoryIcon /> : <AutoAwesomeIcon />}
        sx={{
          borderColor: 'divider',
          color: 'text.secondary',
          bgcolor: 'background.paper',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: 'primary.main', color: 'primary.dark', bgcolor: 'background.paper' },
        }}
      >
        {isClassicTarget
          ? t('versionToggle.classic', 'Classic view')
          : t('versionToggle.new', 'New view')}
      </Button>
    </Tooltip>
  );
};

export default VersionToggle;
